import * as Sentry from '@sentry/nextjs'
import http from '@services/HTTP'
import type { ParentHashResolution } from '@utils/product'
import { getVendorHash } from './enrichCartItemsWithCategories'
import type { CartItem } from 'store/checkout'
import { type VariationAttribute, type iVariation, titles } from 'types/variation'

/** A cart item sent with a parent product hash, and the item to hold instead (null to remove it). */
export type ParentHashChange = { from: CartItem; to: CartItem | null }

const toVariationItem = (item: CartItem, parentHash: string, variant: iVariation): CartItem => ({
  ...item,
  id: variant.hash,
  parentId: parentHash,
  price: variant.price,
  image: item.image || variant.image,
  subscription: variant.subscription,
  variation: Object.entries(variant)
    .filter(([key]) => Object.hasOwn(titles, key))
    .map(([key, value]) => ({
      attribute: key,
      value: (value as VariationAttribute).slug,
      label: (value as VariationAttribute).label
    }))
})

// Items added in Connect carry a variation hash that differs from their parent.
const mayHaveParentHash = (item: CartItem) => item.id === item.parentId || !item.variation?.length

/** Cart items whose `id` is a parent product hash (a vendor site's add to cart can send one), with the item to hold instead. */
export const resolveParentHashItems = async (items: CartItem[]): Promise<ParentHashChange[]> => {
  const hashes = [...new Set(items.filter(mayHaveParentHash).map(item => item.id))]
  if (!hashes.length || !getVendorHash()) return []

  let resolutions: ParentHashResolution[]
  try {
    resolutions = await http.post<ParentHashResolution[], ParentHashResolution[]>(
      '/api/products/resolve-parents',
      { hashes },
      { baseURL: '', headers: { Authorization: null } }
    )
  } catch (e) {
    Sentry.captureException(e, { tags: { source: 'resolveParentHashItems' } })
    return []
  }

  if (!resolutions?.length) return []

  const byHash = new Map(resolutions.map(resolution => [resolution.hash, resolution]))
  const changes = items.flatMap((item): ParentHashChange[] => {
    const resolution = byHash.get(item.id)
    if (!resolution) return []
    return [{ from: item, to: resolution.variant ? toVariationItem(item, resolution.hash, resolution.variant) : null }]
  })

  if (changes.length) {
    Sentry.captureMessage('Cart item sent with a parent product hash', {
      level: 'warning',
      extra: { parentHashes: changes.map(change => change.from.id), replaced: changes.filter(change => change.to).length }
    })
  }

  return changes
}
